import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserSelector } from '@/components/shared/UserSelector';
import { useDMZConfigStore } from '@/stores/dmzConfigStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useCloudServiceStore } from '@/stores/cloudServiceStore';
import { FIREWALL_CHAINS, FIREWALL_ACTIONS, PROTOCOLS, type FirewallRule, type DMZParentType } from '@/types/dmzConfig';
import { Shield, Plus, Trash2, AlertCircle } from 'lucide-react';
import { isValidFirewallAddress, isValidPort } from '@/utils/networkValidation';
import { cn } from '@/lib/utils';

interface DMZEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configId?: string;
  initialData?: {
    label?: string;
    description?: string;
    owner_id?: string;
    parent_node_id?: string;
    position_x?: number;
    position_y?: number;
    site_id?: string;
    zone_id?: string;
    system_id?: string;
    cloud_service_id?: string;
  };
  onSave?: (data: any) => void;
}

interface LocalRule {
  id?: string;
  chain: string;
  source: string;
  destination: string;
  port: string;
  protocol: string;
  action: string;
  notes: string;
  isNew?: boolean;
  isDeleted?: boolean;
  // Validation states
  sourceValid?: boolean;
  destinationValid?: boolean;
  portValid?: boolean;
}

export function DMZEditDialog({
  open,
  onOpenChange,
  configId,
  initialData,
  onSave,
}: DMZEditDialogProps) {
  const { addConfig, updateConfig, getConfigById, addRule, updateRule, deleteRule } = useDMZConfigStore();
  const { sites } = useSiteStore();
  const { zones } = useZoneStore();
  const { systems } = useSystemStore();
  const { cloudServices } = useCloudServiceStore();

  const [label, setLabel] = useState('DMZ');
  const [description, setDescription] = useState('');
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [parentType, setParentType] = useState<DMZParentType>('site');
  const [siteId, setSiteId] = useState<string | undefined>();
  const [zoneId, setZoneId] = useState<string | undefined>();
  const [systemId, setSystemId] = useState<string | undefined>();
  const [cloudServiceId, setCloudServiceId] = useState<string | undefined>();
  const [rules, setRules] = useState<LocalRule[]>([]);
  const [saving, setSaving] = useState(false);

  // Filter zones and systems based on selected parent
  const availableZones = useMemo(() => {
    if (!siteId) return zones;
    return zones.filter(z => z.site_id === siteId);
  }, [zones, siteId]);

  const availableSystems = useMemo(() => {
    if (!zoneId) return systems;
    return systems.filter(s => s.zone_id === zoneId);
  }, [systems, zoneId]);

  useEffect(() => {
    if (configId) {
      const config = getConfigById(configId);
      if (config) {
        setLabel(config.label);
        setDescription(config.description || '');
        setOwnerId(config.owner_id || undefined);
        setRules(
          (config.rules || []).map((r: FirewallRule) => ({
            id: r.id,
            chain: r.chain,
            source: r.source,
            destination: r.destination,
            port: r.port || '',
            protocol: r.protocol,
            action: r.action,
            notes: r.notes || '',
          }))
        );
        
        // Determine parent type from existing data
        if (config.cloud_service_id) {
          setParentType('cloud_service');
          setCloudServiceId(config.cloud_service_id);
        } else if (config.system_id) {
          setParentType('system');
          setSystemId(config.system_id);
          const system = systems.find(s => s.id === config.system_id);
          if (system) {
            setZoneId(system.zone_id);
            const zone = zones.find(z => z.id === system.zone_id);
            if (zone) setSiteId(zone.site_id);
          }
        } else if (config.zone_id) {
          setParentType('zone');
          setZoneId(config.zone_id);
          const zone = zones.find(z => z.id === config.zone_id);
          if (zone) setSiteId(zone.site_id);
        } else {
          setParentType('site');
          setSiteId(config.site_id || undefined);
        }
      }
    } else if (initialData) {
      setLabel(initialData.label || 'DMZ');
      setDescription(initialData.description || '');
      setOwnerId(initialData.owner_id || undefined);
      setRules([]);
      
      if (initialData.cloud_service_id) {
        setParentType('cloud_service');
        setCloudServiceId(initialData.cloud_service_id);
      } else if (initialData.system_id) {
        setParentType('system');
        setSystemId(initialData.system_id);
        const system = systems.find(s => s.id === initialData.system_id);
        if (system) {
          setZoneId(system.zone_id);
          const zone = zones.find(z => z.id === system.zone_id);
          if (zone) setSiteId(zone.site_id);
        }
      } else if (initialData.zone_id) {
        setParentType('zone');
        setZoneId(initialData.zone_id);
        const zone = zones.find(z => z.id === initialData.zone_id);
        if (zone) setSiteId(zone.site_id);
      } else if (initialData.site_id) {
        setParentType('site');
        setSiteId(initialData.site_id);
      }
    } else {
      setLabel('DMZ');
      setDescription('');
      setOwnerId(undefined);
      setRules([]);
      setParentType('site');
      setSiteId(undefined);
      setZoneId(undefined);
      setSystemId(undefined);
      setCloudServiceId(undefined);
    }
  }, [configId, initialData, getConfigById, open, systems, zones]);

  const handleParentTypeChange = (type: DMZParentType) => {
    setParentType(type);
    // Clear child selections when parent type changes
    if (type === 'site') {
      setZoneId(undefined);
      setSystemId(undefined);
      setCloudServiceId(undefined);
    } else if (type === 'zone') {
      setSystemId(undefined);
      setCloudServiceId(undefined);
    } else if (type === 'system') {
      setCloudServiceId(undefined);
    } else if (type === 'cloud_service') {
      setSiteId(undefined);
      setZoneId(undefined);
      setSystemId(undefined);
    }
  };

  const handleAddRule = () => {
    setRules([
      ...rules,
      {
        chain: 'INPUT',
        source: 'any',
        destination: 'any',
        port: '',
        protocol: 'TCP',
        action: 'ALLOW',
        notes: '',
        isNew: true,
      },
    ]);
  };

  const handleUpdateLocalRule = (index: number, field: keyof LocalRule, value: string) => {
    const updated = [...rules];
    const rule = { ...updated[index], [field]: value };
    
    // Validate on change
    if (field === 'source') {
      rule.sourceValid = !value || isValidFirewallAddress(value);
    } else if (field === 'destination') {
      rule.destinationValid = !value || isValidFirewallAddress(value);
    } else if (field === 'port') {
      rule.portValid = !value || isValidPort(value);
    }
    
    updated[index] = rule;
    setRules(updated);
  };

  // Check if any rules have validation errors
  const hasRuleValidationErrors = useMemo(() => {
    return rules.some(r => !r.isDeleted && (
      (r.source && r.sourceValid === false) ||
      (r.destination && r.destinationValid === false) ||
      (r.port && r.portValid === false)
    ));
  }, [rules]);

  const handleDeleteLocalRule = (index: number) => {
    const rule = rules[index];
    if (rule.id) {
      const updated = [...rules];
      updated[index] = { ...updated[index], isDeleted: true };
      setRules(updated);
    } else {
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const configData = {
        label,
        description: description || null,
        owner_id: ownerId || null,
        parent_node_id: initialData?.parent_node_id,
        position_x: initialData?.position_x,
        position_y: initialData?.position_y,
        // Set parent IDs based on parent type
        site_id: parentType === 'site' ? siteId : (parentType === 'zone' || parentType === 'system' ? siteId : null),
        zone_id: parentType === 'zone' ? zoneId : (parentType === 'system' ? zoneId : null),
        system_id: parentType === 'system' ? systemId : null,
        cloud_service_id: parentType === 'cloud_service' ? cloudServiceId : null,
      };

      let targetConfigId = configId;

      if (configId) {
        await updateConfig(configId, configData);
      } else {
        const result = await addConfig(configData);
        if (result) {
          targetConfigId = result.id;
          if (onSave) {
            onSave({ ...configData, id: result.id });
          }
        }
      }

      // Handle rules if we have a config ID
      let finalRulesCount = 0;
      if (targetConfigId) {
        for (const rule of rules) {
          if (rule.isDeleted && rule.id) {
            await deleteRule(rule.id);
          } else if (rule.isNew && !rule.isDeleted) {
            await addRule({
              dmz_config_id: targetConfigId,
              chain: rule.chain as any,
              source: rule.source,
              destination: rule.destination,
              port: rule.port || null,
              protocol: rule.protocol,
              action: rule.action as any,
              notes: rule.notes || null,
            });
            finalRulesCount++;
          } else if (rule.id && !rule.isDeleted) {
            await updateRule(rule.id, {
              chain: rule.chain as any,
              source: rule.source,
              destination: rule.destination,
              port: rule.port || null,
              protocol: rule.protocol,
              action: rule.action as any,
              notes: rule.notes || null,
            });
            finalRulesCount++;
          }
        }
      }

      if (onSave && targetConfigId) {
        onSave({ ...configData, id: targetConfigId, rulesCount: finalRulesCount });
      }

      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const visibleRules = rules.filter((r) => !r.isDeleted);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-500" />
            {configId ? 'Edit DMZ Configuration' : 'Add DMZ Configuration'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="label">Name</Label>
              <Input
                id="label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., External DMZ, Internal DMZ"
              />
            </div>
            <UserSelector
              value={ownerId}
              onChange={setOwnerId}
              label="Owner"
              placeholder="Assign an owner..."
              allowUnassigned
            />
          </div>

          {/* Parent Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Parent Level</Label>
              <Select value={parentType} onValueChange={(v) => handleParentTypeChange(v as DMZParentType)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select parent level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="site">Site</SelectItem>
                  <SelectItem value="zone">Zone</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="cloud_service">Cloud Service</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Parent selector based on type */}
            {parentType === 'cloud_service' ? (
              <div className="space-y-2">
                <Label>Cloud Service</Label>
                <Select value={cloudServiceId || ''} onValueChange={setCloudServiceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select cloud service" />
                  </SelectTrigger>
                  <SelectContent>
                    {cloudServices.map(cs => (
                      <SelectItem key={cs.id} value={cs.id}>{cs.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Site</Label>
                <Select value={siteId || ''} onValueChange={setSiteId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Zone selector - shown for zone and system parent types */}
          {(parentType === 'zone' || parentType === 'system') && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zone</Label>
                <Select value={zoneId || ''} onValueChange={setZoneId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select zone" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableZones.map(zone => (
                      <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* System selector - shown for system parent type */}
              {parentType === 'system' && (
                <div className="space-y-2">
                  <Label>System</Label>
                  <Select value={systemId || ''} onValueChange={setSystemId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select system" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSystems.map(system => (
                        <SelectItem key={system.id} value={system.id}>{system.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional notes about this DMZ..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Firewall Rules</Label>
              <Button size="sm" variant="outline" onClick={handleAddRule}>
                <Plus className="h-4 w-4 mr-1" />
                Add Rule
              </Button>
            </div>

            {visibleRules.length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Chain</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead className="w-[80px]">Port</TableHead>
                      <TableHead className="w-[80px]">Protocol</TableHead>
                      <TableHead className="w-[80px]">Action</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((rule, index) =>
                      rule.isDeleted ? null : (
                        <TableRow key={rule.id || `new-${index}`}>
                          <TableCell>
                            <Select
                              value={rule.chain}
                              onValueChange={(v) => handleUpdateLocalRule(index, 'chain', v)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FIREWALL_CHAINS.map((c) => (
                                  <SelectItem key={c} value={c}>
                                    {c}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="relative">
                              <Input
                                value={rule.source}
                                onChange={(e) => handleUpdateLocalRule(index, 'source', e.target.value)}
                                placeholder="any / IP / CIDR"
                                className={cn("h-8", rule.sourceValid === false && "border-destructive pr-8")}
                              />
                              {rule.sourceValid === false && (
                                <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="relative">
                              <Input
                                value={rule.destination}
                                onChange={(e) => handleUpdateLocalRule(index, 'destination', e.target.value)}
                                placeholder="any / IP / CIDR"
                                className={cn("h-8", rule.destinationValid === false && "border-destructive pr-8")}
                              />
                              {rule.destinationValid === false && (
                                <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="relative">
                              <Input
                                value={rule.port}
                                onChange={(e) => handleUpdateLocalRule(index, 'port', e.target.value)}
                                placeholder="80, 443, 1000-2000"
                                className={cn("h-8", rule.portValid === false && "border-destructive pr-8")}
                              />
                              {rule.portValid === false && (
                                <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={rule.protocol}
                              onValueChange={(v) => handleUpdateLocalRule(index, 'protocol', v)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PROTOCOLS.map((p) => (
                                  <SelectItem key={p} value={p}>
                                    {p}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={rule.action}
                              onValueChange={(v) => handleUpdateLocalRule(index, 'action', v)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FIREWALL_ACTIONS.map((a) => (
                                  <SelectItem key={a} value={a}>
                                    {a}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDeleteLocalRule(index)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                No firewall rules defined. Click "Add Rule" to create one.
              </p>
            )}
          </div>
        </div>

        {hasRuleValidationErrors && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            Some firewall rules have invalid values. Use "any", IP addresses, or CIDR notation for source/destination.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !label.trim() || hasRuleValidationErrors}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
